import "./App.css";
import ModernNavBar from "./components/main/ModernNavBar";
import { AuthContextProvider } from "./context/AuthContext";
import { Route, Routes } from "react-router-dom";
import ProtectedRoute from "./auth/ProtectedRoute";
import { createModernTheme } from "./theme/modernTheme";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useState, Suspense, lazy } from "react";
import { NotificationProvider } from './context/NotificationContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CircularProgress, Box, Typography } from '@mui/material';

// Lazy-loaded components for code splitting
const Tracking = lazy(() => import("./pages/tracking/Tracking"));
const TrackingMobile = lazy(() => import("./pages/tracking/trackingMobile"));
const Login = lazy(() => import("./auth/Login"));
const Home = lazy(() => import("./pages/home/Home"));
const Inventory = lazy(() => import("./pages/inventory/Inventory"));
const Settings = lazy(() => import("./pages/settings/Settings"));
const Dashboard = lazy(() => import("./pages/homepage/Dashboard"));
const Delivery = lazy(() => import("./pages/delivery/Delivery"));
const MDelivery = lazy(() => import("./pages/delivery/mDelivery"));
const Vouchers = lazy(() => import("./pages/vouchers/Vouchers"));
const History = lazy(() => import("./pages/history/History"));
const CsvToFirestore = lazy(() => import("./pages/settings/CsvToFirestore"));
const JSONtoFirestore = lazy(() => import("./pages/settings/JsonToFirestore"));
const Vendors = lazy(() => import("./pages/vendors/Vendors"));
const Customers = lazy(() => import("./pages/customers/Customers"));
const Global = lazy(() => import("./pages/settings/Global"));
const Movimec = lazy(() => import("./pages/movimec/Movimec"));
const Test = lazy(() => import("./pages/movimec/test"));
const Payable = lazy(() => import("./pages/payable/Payable"));
const Receivable = lazy(() => import("./pages/receivable/Receivable"));
const NotFound = lazy(() => import("./pages/home/404"));
const GraphData = lazy(() => import("./pages/graph/Graph"));
const Statistics = lazy(() => import("./pages/graph/Statistics"));
const TransactionsDashboard = lazy(() => import("./pages/graph/TransactionsDashboard"));
const CustomTable = lazy(() => import("./pages/settings/CustomTable"));
const Links = lazy(() => import("./pages/settings/Links"));
const ChecksView = lazy(() => import('./pages/checks/ChecksView'));
const FollowUp = lazy(() => import("./pages/followup/FollowUp"));
const Todo = lazy(() => import("./pages/todo/Todo"));
const Generator = lazy(() => import("./pages/generator/Generator"));
const FreightCostCalc = lazy(() => import("./pages/calculator/FreightCostCalc"));
const CustomRoutes = lazy(() => import("./pages/croutes/CustomRoutes"));
const PurchaseOrder = lazy(() => import("./pages/PurchaseOrder/PurchaseOrder"));
const Proforma = lazy(() => import("./pages/proforma/Proforma"));
const Shopify = lazy(() => import("./pages/settings/Shopify"));
const FetchShopify = lazy(() => import("./pages/settings/FetchShopify"));
const Users = lazy(() => import("./pages/users/Users"));
const Main = lazy(() => import("./pages/main/Main"));
const Bearingnet = lazy(() => import("./pages/bearingnet/Bearingnet"));
const BearingnetRFQ = lazy(() => import("./pages/bearingnet/BearingnetRFQ"));
const Packing = lazy(() => import("./pages/packing/Packing"));
const PayPal = lazy(() => import("./pages/payment/Paypal"));
const CustomerDash = lazy(() => import("./pages/customers/CustomerDash"));
const SearchDuplicates = lazy(() => import('./pages/settings/SearchDuplicates'));
const TestingCode = lazy(() => import('./pages/testing/TestingCode'));
const PoSelection = lazy(() => import("./pages/PurchaseOrder/poSelection"));
const Transaction = lazy(() => import("./pages/transaction/Transaction"));
const CheckDateChange = lazy(() => import("./pages/settings/CheckDateChange"));
const InventoryControl = lazy(() => import("./pages/inventory/InventoryControl"));
const BearingnetInventory = lazy(() => import("./pages/bearingnet/BearingnetInventory"));
const CloneCollection = lazy(() => import('./pages/settings/CloneCollection'));
const VendorDash = lazy(() => import("./pages/vendors/VendorDash"));
const CollectionStructure = lazy(() => import("./pages/settings/CollectionStructure"));
const CarrierSettings = lazy(() => import("./pages/settings/CarrierSettings"));
const Log = lazy(() => import("./pages/log/Log"));
const Inbox = lazy(() => import("./pages/inbox/Inbox"));

const ShopifyDescriptionGenerator = lazy(() => import("./pages/shopify/ShopifyDescriptionGenerator"));
const ShopifyProducts = lazy(() => import("./pages/shopify/ShopifyProducts"));
const PostgresImport = lazy(() => import("./pages/settings/Postgresimport"));
const FirestoreExport = lazy(() => import("./pages/settings/FirestoreExport"));
// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "50vh",
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  const [colorMode, setColorMode] = useState(true);
  
  try {
    // Use the modern theme
    const modernTheme = createModernTheme(colorMode ? 'dark' : 'light');

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthContextProvider>
          <NotificationProvider>
            <ThemeProvider theme={modernTheme}>
              <CssBaseline />
              <div>
                <ModernNavBar colorMode={colorMode} setColorMode={setColorMode} />
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<Dashboard />} />
                <Route path="/test" element={<Test />} />
                <Route path="/links" element={<Links />} />
                <Route path="/generator" element={<Generator />} />
                <Route path="/freight-calculator" element={<FreightCostCalc />} /> 
                <Route path="/testing" element={<TestingCode />} />
                <Route path="/clone-collection" element={<CloneCollection />} />
                <Route path="*" element={<NotFound/>} />
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      <Home />
                    </ProtectedRoute>
                  }
                />
               <Route
                  path="/main"
                  element={
                    <ProtectedRoute>
                      <Main />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute>
                      <Inventory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory-control"
                  element={
                    <ProtectedRoute>
                      <InventoryControl />
                    </ProtectedRoute>
                  }
                />
                 <Route
                  path="/duplicates"
                  element={
                    <ProtectedRoute>
                      <SearchDuplicates />
                    </ProtectedRoute>
                  }
                />   
                <Route
                  path="/collection-structure"
                  element={
                    <ProtectedRoute>
                      <CollectionStructure />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vendors-dashboard"
                  element={
                    <ProtectedRoute>
                      <VendorDash />
                    </ProtectedRoute>
                  }
                />

<Route
                  path="/postgres-import"
                  element={
                    <ProtectedRoute>
                      <PostgresImport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/transaction"
                  element={
                    <ProtectedRoute>
                      <Transaction />
                    </ProtectedRoute>
                  }
                />
            
                <Route
                  path="/bearingnet-inventory"
                  element={
                    <ProtectedRoute>
                      <BearingnetInventory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/firestore-export"
                  element={
                    <ProtectedRoute>
                      < FirestoreExport />  
                    </ProtectedRoute>
                  }
                />
                <Route  
                  path="/BearingnetRFQ"
                  element={
                    <ProtectedRoute>
                      <BearingnetRFQ />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/po-selection"
                  element={
                    <ProtectedRoute>
                      <PoSelection />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory-control"
                  element={
                    <ProtectedRoute>
                      <InventoryControl />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/check-date-change"
                  element={
                    <ProtectedRoute>
                      <CheckDateChange />
                    </ProtectedRoute>
                  }
                />
           
                <Route
                  path="/bearingnet"
                  element={
                    <ProtectedRoute>
                      <Bearingnet />
                    </ProtectedRoute>
                  }
                />
           
                <Route
                  path="/packing-slip"
                  element={
                    <ProtectedRoute>
                      <Packing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customer-dash"
                  element={
                    <ProtectedRoute>
                      <CustomerDash />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/proforma"
                  element={
                    <ProtectedRoute>
                      <Proforma />
                    </ProtectedRoute>
                  }
                />
                    <Route
                  path="/paypal"
                  element={
                    <ProtectedRoute>
                      <PayPal />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <Users />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/purchase-order"
                  element={
                    <ProtectedRoute>
                      <PurchaseOrder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/history"
                  element={
                    <ProtectedRoute>
                      <History />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customtable"
                  element={
                    <ProtectedRoute>
                      <CustomTable />
                    </ProtectedRoute>
                  }
                />
               <Route
                  path="/graphs"
                  element={
                    <ProtectedRoute>
                     <GraphData />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/statistics"
                  element={
                    <ProtectedRoute>
                     <Statistics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/transactions-dashboard"
                  element={
                    <ProtectedRoute>
                      <TransactionsDashboard />
                    </ProtectedRoute>
                  }
                />
              <Route
                  path="/fetchshopify"
                  element={
                    <ProtectedRoute>
                     <FetchShopify />
                    </ProtectedRoute>
                  }
                />

               <Route
                  path="/vouchers"
                  element={
                    <ProtectedRoute>
                     <Vouchers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/delivery"
                  element={
                    <ProtectedRoute>
                      <Delivery />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/mdelivery"
                  element={
                    <ProtectedRoute>
                      <MDelivery />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/carrier-settings"
                  element={
                    <ProtectedRoute>
                      <CarrierSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/custom-routes"
                  element={
                    <ProtectedRoute>
                      <CustomRoutes />
                    </ProtectedRoute>
                  }
                />
               <Route
                  path="/csv-to-firestore"
                  element={
                    <ProtectedRoute>
                      <CsvToFirestore />
                    </ProtectedRoute>
                  }
                />
               <Route
                  path="/payable"
                  element={
                    <ProtectedRoute>
                      <Payable />
                    </ProtectedRoute>
                  }
                />
               <Route
                  path="/receivable"
                  element={
                    <ProtectedRoute>
                      <Receivable />
                    </ProtectedRoute>
                  }
                />


                <Route
                  path="/json-to-firestore"
                  element={
                    <ProtectedRoute>
                      <JSONtoFirestore />
                    </ProtectedRoute>
                  }
                />
               <Route
                  path="/vendors"
                  element={
                    <ProtectedRoute>
                      <Vendors />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute>
                      <Customers />
                    </ProtectedRoute>
                  }
                />
               <Route
                  path="/vouchers"
                  element={
                    <ProtectedRoute>
                      <Vouchers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/global"
                  element={
                    <ProtectedRoute>
                      <Global />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify"
                  element={
                    <ProtectedRoute>
                      <Shopify />
                    </ProtectedRoute>
                  }
                />
               <Route
                  path="/movimec"
                  element={
                    <ProtectedRoute>
                      <Movimec />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/checks"
                  element={
                    <ProtectedRoute>
                      <ChecksView />
                    </ProtectedRoute>
                  }
                />      
                
                <Route
                  path="/followup"
                  element={
                    <ProtectedRoute>
                      <FollowUp />
                    </ProtectedRoute>
                  }
                />       
                         <Route
                  path="/todo"
                  element={
                    <ProtectedRoute>
                      <Todo />
                    </ProtectedRoute>
                  }
                />    
                <Route
                  path="/search-duplicates"
                  element={
                    <ProtectedRoute>
                      <SearchDuplicates />
                    </ProtectedRoute>
                  }
                />
                <Route path="/testing" element={<TestingCode />} />
                <Route path="/settings/clone-collection" element={<CloneCollection />} />
                <Route
                  path="/log"
                  element={
                    <ProtectedRoute>
                      <Log />
                    </ProtectedRoute>
                  }
                />
                <Route path="/tracking" element={<Tracking />} />
                <Route path="/trackingMobile" element={<TrackingMobile />} />
                <Route
                  path="/inbox"
                  element={
                    <ProtectedRoute>
                      <Inbox />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopify-description-generator"
                  element={
                    <ProtectedRoute>
                      <ShopifyDescriptionGenerator />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shopifyProducts"
                  element={
                    <ProtectedRoute>
                      <ShopifyProducts />
                    </ProtectedRoute>
                  }
                />
                </Routes>
              </Suspense>
            </div>
          </ThemeProvider>
        </NotificationProvider>
      </AuthContextProvider>
    </LocalizationProvider>
  );
  } catch (error) {
    console.error('App Error:', error);
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h4" color="error">Something went wrong</Typography>
        <Typography variant="body1">Error: {error.message}</Typography>
        <Typography variant="body2">Check the browser console for details.</Typography>
      </Box>
    );
  }
}

export default App;
